import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload } from "lucide-react";
import { logWarn } from "@/lib/logger";

/** Authenticated <img> that fetches via API with cookies to display object storage files. */
export function AuthImage({ fileId, className, alt = "" }) {
  const [src, setSrc] = useState(null);
  const revoked = useRef(false);

  useEffect(() => {
    if (!fileId) return;
    let objectUrl;
    revoked.current = false;
    (async () => {
      try {
        const resp = await api.get(`/files/${fileId}`, { responseType: "blob" });
        objectUrl = URL.createObjectURL(resp.data);
        if (!revoked.current) setSrc(objectUrl);
      } catch (e) { logWarn("AuthImage: failed to load", fileId, e); }
    })();
    return () => {
      revoked.current = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fileId]);

  if (!src) return <div className={`bg-[#EBE3D3]/40 ${className}`} />;
  return <img src={src} alt={alt} className={className} />;
}

/** Simple file drop-zone that uploads to /files/upload and returns file_id via onUploaded. */
export function FileDropzone({ kind = "image", accept = "image/*", label, onUploaded, testId }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", kind);
        const { data } = await api.post("/files/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        onUploaded?.(data);
      }
    } catch (e) {
      logWarn("FileDropzone: upload failed", e);
      toast.error(e?.response?.data?.detail || "Upload fallito");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="w-full">
      <button
        type="button"
        data-testid={testId}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
        className="w-full border-2 border-dashed border-white/15 rounded-lg p-8 text-center bg-[#FFFAF0] hover:bg-[#F0E7D3] hover:border-[#C5A880] transition-colors duration-200"
        disabled={busy}
      >
        <Upload className="mx-auto h-6 w-6 text-[#C5A880] mb-3" />
        <p className="text-sm text-[#2E2A22]">{busy ? "…" : label}</p>
        <p className="text-xs text-[#9A8B72] mt-1">{accept}</p>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={kind === "image"}
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
    </div>
  );
}

export { fileUrl };
